import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { locationsState, memberState } from "../../../../state/atoms";
import { LocationDto } from "../../../../generated/dashboard-client";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitAssignmentDto, DataKitDto } from "../../../../domain/DataKitDto";
import { dataKitThumbColors } from "./DataKitThumb";
import { locationLabel } from "./DataKitManageLocationsModal";
import "./DataKitLocationAssignmentsTab.scss";

const PAGE_SIZE = 10;

interface Row {
  location: LocationDto;
  kits: DataKitDto[];
}

interface Props {
  kits: DataKitDto[];
  onChanged: () => void;
}

export function DataKitLocationAssignmentsTab({ kits, onChanged }: Props) {
  const locations = useRecoilValue(locationsState);
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [assignmentsByKit, setAssignmentsByKit] = useState<Record<string, DataKitAssignmentDto[]>>({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // One modal for both "Assign" and "Edit" (Figma 199:13059 / 199:13303): the location's kits as a
  // checklist, so adding and removing are the same gesture.
  // Two steps (Ian, Oct 5): Assign / Edit opens "Kits this location uses" - remove what's there,
  // or "+ Assign another kit" to open the checklist. Held by location id rather than as a Row so
  // the first step re-reads the location's kits after every change instead of showing a stale copy.
  const [usesLocationId, setUsesLocationId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [assignRow, setAssignRow] = useState<Row | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const activeKits = useMemo(() => kits.filter((k) => !k.archived), [kits]);

  const loadAssignments = useCallback(() => {
    const withIds = kits.filter((k) => !!k.id);
    if (withIds.length === 0) {
      setAssignmentsByKit({});
      return;
    }
    setLoading(true);
    Promise.all(
      withIds.map((k) =>
        DataKitApi.listAssignments(k.id as string)
          .then((res) => [k.id as string, res.data || []] as const)
          .catch(() => [k.id as string, [] as DataKitAssignmentDto[]] as const)
      )
    )
      .then((entries) => setAssignmentsByKit(Object.fromEntries(entries)))
      .finally(() => setLoading(false));
  }, [kits]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const kitsByLocation = useMemo(() => {
    const byId = new Map(kits.filter((k) => k.id).map((k) => [k.id as string, k]));
    const map = new Map<string, DataKitDto[]>();
    Object.entries(assignmentsByKit).forEach(([kitId, assignments]) => {
      const kit = byId.get(kitId);
      if (!kit) return;
      assignments
        .filter((a) => a.unitType === "LOCATION")
        .forEach((a) => {
          const list = map.get(a.unitId) || [];
          list.push(kit);
          map.set(a.unitId, list);
        });
    });
    return map;
  }, [assignmentsByKit, kits]);

  const rows: Row[] = useMemo(
    () =>
      locations.map((location) => ({
        location,
        kits: kitsByLocation.get(String(location.internalId)) || [],
      })),
    [locations, kitsByLocation]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => !q || locationLabel(r.location).toLowerCase().includes(q));
  }, [rows, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const openAssign = (row: Row) => {
    setAssignRow(row);
    setChecked(new Set(row.kits.map((k) => k.id as string)));
  };

  /** Kits ticked now that weren't when the modal opened - and the other way round. */
  const changes = useMemo(() => {
    if (!assignRow) return { added: [] as DataKitDto[], removed: [] as DataKitDto[] };
    const before = new Set(assignRow.kits.map((k) => k.id as string));
    return {
      added: activeKits.filter((k) => checked.has(k.id as string) && !before.has(k.id as string)),
      removed: assignRow.kits.filter((k) => !checked.has(k.id as string)),
    };
  }, [assignRow, checked, activeKits]);

  const confirmAssign = () => {
    if (!assignRow) return;
    const unitId = String(assignRow.location.internalId);
    const { added, removed } = changes;
    if (added.length === 0 && removed.length === 0) {
      setAssignRow(null);
      return;
    }
    setSaving(true);
    // In sequence: every call rewrites one kit's assignment set, and nothing on the server
    // serialises concurrent writes to the same location for us.
    const steps = [
      ...added.map((k) => () => DataKitApi.createAssignment(k.id as string, { unitType: "LOCATION", unitId })),
      ...removed.map((k) => () => DataKitApi.deleteAssignment(k.id as string, "LOCATION", unitId)),
    ];
    steps
      .reduce<Promise<unknown>>((chain, step) => chain.then(step), Promise.resolve())
      .then(() => {
        setAssignRow(null);
        loadAssignments();
        onChanged();
      })
      .catch(() => message.error("Could not save this location's item libraries"))
      .finally(() => setSaving(false));
  };

  const usesRow = useMemo(
    () => (usesLocationId ? rows.find((r) => String(r.location.internalId) === usesLocationId) || null : null),
    [rows, usesLocationId]
  );

  // Takes effect straight away, like the old per-chip remove. Unassigning disconnects the kit's
  // items on designs at this location: they keep today's values and stop getting kit updates, and
  // reassigning reconnects them - which is why there is no second confirmation step here.
  const removeKit = (row: Row, kit: DataKitDto) => {
    if (!kit.id) return;
    setRemoving(kit.id);
    DataKitApi.deleteAssignment(kit.id, "LOCATION", String(row.location.internalId))
      .then(() => {
        message.success(`${kit.name || "Library"} removed from ${locationLabel(row.location)}`);
        loadAssignments();
        onChanged();
      })
      .catch(() => message.error("Could not remove this library"))
      .finally(() => setRemoving(null));
  };

  const columns = [
    {
      title: "Location",
      key: "location",
      sorter: (a: Row, b: Row) => locationLabel(a.location).localeCompare(locationLabel(b.location)),
      showSorterTooltip: false,
      // One line, as the mock reads it - a location's own name already says where it is
      // ("Austin — Downtown, TX"), so the address line underneath only repeated it.
      render: (_: unknown, row: Row) => (
        <span className="data-kit-location-name">{locationLabel(row.location)}</span>
      ),
    },
    {
      title: "Item libraries",
      key: "kits",
      render: (_: unknown, row: Row) =>
        row.kits.length === 0 ? (
          <span className="data-kit-kit-chip none">No libraries assigned</span>
        ) : (
          <div className="data-kit-chips">
            {row.kits.map((kit) => {
              // The kit's own thumbnail colours, so a kit reads the same here as on the list.
              const { bg, fg } = dataKitThumbColors(kit.name || "Untitled");
              return (
                <span
                  key={kit.id}
                  className="data-kit-kit-chip"
                  style={{ background: bg, color: fg, borderColor: `${fg}33` }}
                >
                  {kit.name || "Untitled"}
                </span>
              );
            })}
          </div>
        ),
    },
    {
      title: "",
      key: "action",
      width: 100,
      align: "right" as const,
      render: (_: unknown, row: Row) =>
        canWrite ? (
          row.kits.length === 0 ? (
            <Button className="data-kit-assign-btn primary" onClick={() => setUsesLocationId(String(row.location.internalId))}>
              Assign
            </Button>
          ) : (
            <Button className="data-kit-edit-btn" onClick={() => setUsesLocationId(String(row.location.internalId))}>
              Edit
            </Button>
          )
        ) : null,
    },
  ];

  return (
    <div className="data-kit-location-assignments-tab">
      {/* Search stays even though the mock has none: accounts run to a hundred-plus locations, and
          it costs the layout nothing. The All / Assigned / Unassigned switch and the count pills
          are gone - the chip on each row already says which is which. */}
      <div className="data-kit-assignments-toolbar">
        <Input
          className="data-kit-assignments-search"
          size="large"
          placeholder="Search locations"
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
      </div>

      <Table
        className="data-kit-assignments-table"
        dataSource={pageItems}
        columns={columns}
        loading={loading}
        rowKey={(row) => String(row.location.internalId)}
        pagination={
          filtered.length > PAGE_SIZE
            ? {
                current: page,
                pageSize: PAGE_SIZE,
                total: filtered.length,
                onChange: setPage,
                showSizeChanger: false,
              }
            : false
        }
        locale={{ emptyText: "No locations match your filters" }}
      />

      <Modal
        className={"data-kit-uses-modal clean one with-close"}
        open={!!usesRow && !assignRow}
        width={520}
        onCancel={() => setUsesLocationId(null)}
      >
        <header>
          <h2>{usesRow ? locationLabel(usesRow.location) : ""}</h2>
        </header>
        <p className="data-kit-assign-sub">Item libraries this location uses</p>
        <ul className="data-kit-uses-list">
          {(usesRow?.kits || []).map((kit) => {
            const { bg, fg } = dataKitThumbColors(kit.name || "Untitled");
            return (
              <li key={kit.id}>
                <span
                  className="data-kit-kit-chip"
                  style={{ background: bg, color: fg, borderColor: `${fg}33` }}
                >
                  {kit.name || "Untitled"}
                </span>
                {canWrite && (
                  <Button
                    className="data-kit-edit-btn"
                    loading={removing === kit.id}
                    onClick={() => usesRow && removeKit(usesRow, kit)}
                  >
                    Remove
                  </Button>
                )}
              </li>
            );
          })}
          {usesRow && usesRow.kits.length === 0 && (
            <li className="data-kit-uses-empty">No libraries assigned yet.</li>
          )}
          {canWrite && (
            <li>
              <button
                type="button"
                className="data-kit-uses-add"
                onClick={() => usesRow && openAssign(usesRow)}
              >
                + Assign another library
              </button>
            </li>
          )}
        </ul>
        <footer>
          <Button size="large" className="primary" onClick={() => setUsesLocationId(null)}>
            Done
          </Button>
        </footer>
      </Modal>

      <Modal
        className={"data-kit-assign-modal clean one with-close"}
        open={!!assignRow}
        width={520}
        onCancel={() => setAssignRow(null)}
      >
        <header>
          <h2>{assignRow ? locationLabel(assignRow.location) : ""}</h2>
        </header>
        <p className="data-kit-assign-sub">
          {assignRow && assignRow.kits.length > 0
            ? "Select another library to assign to this location"
            : "Select a library to assign to this location"}
        </p>
        {activeKits.length === 0 ? (
          <p className="data-kit-assign-empty">There are no item libraries to assign yet.</p>
        ) : (
          <ul className="data-kit-assign-list">
            {activeKits.map((kit) => {
              const id = kit.id as string;
              return (
                <li key={id}>
                  <Checkbox
                    checked={checked.has(id)}
                    onChange={(e) =>
                      setChecked((current) => {
                        const next = new Set(current);
                        if (e.target.checked) next.add(id);
                        else next.delete(id);
                        return next;
                      })
                    }
                  >
                    {kit.name || "Untitled"}
                  </Checkbox>
                  <span className="data-kit-assign-count">
                    {kit.itemCount} item{kit.itemCount === 1 ? "" : "s"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {/* Unticking a kit is an unassign, which used to have a confirmation of its own. The
            checklist keeps the consequence on screen instead of adding a second dialog the mock
            doesn't have. */}
        {changes.removed.length > 0 && (
          <p className="data-kit-assign-warning">
            Removing {changes.removed.map((k) => k.name).join(", ")} disconnects{" "}
            {changes.removed.length === 1 ? "it" : "them"} from designs at this location. Those
            designs keep today&apos;s values but stop getting library updates.
          </p>
        )}

        <footer>
          <Button size="large" onClick={() => setAssignRow(null)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={saving} onClick={confirmAssign}>
            Assign
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
